import React, { useEffect } from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import ModiFuelSection from './sections/ModiFuelSection';

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What did Prime Minister Modi say about fuel conservation?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Prime Minister Narendra Modi publicly appealed to all Indians to voluntarily reduce fuel use and reduced his own convoy as a visible first step. He called for a national shift toward cleaner, more energy-efficient transport, with electric vehicles at the centre of that transition."
      }
    },
    {
      "@type": "Question",
      "name": "How do electric vehicles help reduce fuel consumption in India?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "EVs run entirely on electricity, eliminating petrol use for every ride taken. Across millions of daily commutes, this directly reduces India's oil import demand and lowers urban air pollution, contributing to the exact fuel conservation goal Prime Minister Modi called for."
      }
    },
    {
      "@type": "Question",
      "name": "Is India's EV adoption growing in 2026?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Strongly yes. India recorded 24.5 lakh EV sales in FY2026, a 25% year-on-year increase, with March 2026 hitting a record 2,80,093 units. The growth is driven by government policy, GST cut to 5%, over 29,000 charging stations, falling EV prices, and ride-hailing apps giving commuters access without ownership."
      }
    },
    {
      "@type": "Question",
      "name": "How can commuters switch to electric vehicle rides today?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "You do not need to own an electric vehicle to be part of the change. Ride-hailing apps have made electric mobility available to every commuter, anytime, anywhere, with no barriers in the way. The shift can begin today, with nothing more than a single ride."
      }
    },
    {
      "@type": "Question",
      "name": "What does this mean for Chennai commuters in 2026?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "For Chennai commuters, NURA Electric Mobility is that switch. Book an electric auto ride in minutes, get instantly matched with a nearby driver, enjoy fixed pricing with no bargaining, and rely on rides that show up without last-minute cancellations."
      }
    }
  ]
};

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "PM Modi's Fuel Conservation & India's Electric Vehicle Rise | NURA",
  "description": "PM Modi's fuel conservation call is accelerating India's shift to electric vehicles. Discover what it means for Chennai commuters and how to ride smarter.",
  "author": { "@type": "Organization", "name": "Nura eMobility" },
  "publisher": { "@type": "Organization", "name": "Nura eMobility", "url": "https://nuraemobility.co.in" },
  "datePublished": "2026-05-01",
  "dateModified": "2026-05-01",
  "url": "https://nuraemobility.co.in/blog/pm-modi-fuel-conservation-india-electric-vehicles",
  "keywords": "PM Modi fuel conservation, India electric vehicle, EV adoption India 2026, electric auto Chennai, fuel conservation India, reduce fuel consumption"
};

const ModiFuelPage = () => {
  useEffect(() => {
    document.title = "PM Modi's Fuel Conservation & India's Electric Vehicle Rise | NURA";

    const setMeta = (name, content, attr = 'name') => {
      let tag = document.querySelector(`meta[${attr}="${name}"]`);
      if (tag) { tag.setAttribute('content', content); }
      else {
        tag = document.createElement('meta');
        tag.setAttribute(attr, name);
        tag.setAttribute('content', content);
        document.head.appendChild(tag);
      }
    };

    setMeta('description', "PM Modi's fuel conservation call is accelerating India's shift to electric vehicles. Discover what it means for Chennai commuters and how to ride smarter.");
    setMeta('keywords', 'PM Modi fuel conservation, India electric vehicle, EV adoption India 2026, electric auto Chennai, fuel conservation India, reduce fuel consumption');
    setMeta('robots', 'index, follow');

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.setAttribute('rel', 'canonical'); document.head.appendChild(canonical); }
    canonical.setAttribute('href', 'https://nuraemobility.co.in/blog/pm-modi-fuel-conservation-india-electric-vehicles');

    setMeta('og:type', 'article', 'property');
    setMeta('og:title', "PM Modi's Fuel Conservation & India's Electric Vehicle Rise | NURA", 'property');
    setMeta('og:description', "PM Modi's fuel conservation call is accelerating India's shift to electric vehicles. Discover what it means for Chennai commuters and how to ride smarter.", 'property');
    setMeta('og:url', 'https://nuraemobility.co.in/blog/pm-modi-fuel-conservation-india-electric-vehicles', 'property');
    setMeta('og:image', 'https://nuraemobility.co.in/og-modi-fuel.png', 'property');
    setMeta('og:image:width', '1200', 'property');
    setMeta('og:image:height', '627', 'property');
    setMeta('og:site_name', 'Nura eMobility', 'property');

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', "PM Modi's Fuel Conservation & India's Electric Vehicle Rise");
    setMeta('twitter:description', "PM Modi's fuel conservation call is accelerating India's shift to electric vehicles. Discover what it means for Chennai commuters.");
    setMeta('twitter:image', 'https://nuraemobility.co.in/og-modi-fuel.png');

    const injectSchema = (id, data) => {
      let script = document.getElementById(id);
      if (!script) { script = document.createElement('script'); script.id = id; script.type = 'application/ld+json'; document.head.appendChild(script); }
      script.textContent = JSON.stringify(data);
    };
    injectSchema('schema-faq-modi', faqSchema);
    injectSchema('schema-article-modi', articleSchema);

    return () => {
      document.title = 'Nura, Electric Auto Rides';
      ['schema-faq-modi', 'schema-article-modi'].forEach(id => { const el = document.getElementById(id); if (el) el.remove(); });
    };
  }, []);

  return (
    <div className="min-h-screen bg-black">
      <Navbar />
      <div className="pt-20">
        <ModiFuelSection />
      </div>
      <Footer />
    </div>
  );
};

export default ModiFuelPage;
